import paths from "../../../../../api/paths.api";
import { PROCUREMENT_CATEGORIES } from "./treeConstants";

/**
 * Items of one procurement register for a plant.
 *
 * `plantData.pos` / `plantData.bids` are normalized from the plant's MRQs
 * (see normalizeMcsTreeData), but fall back to deriving them here so rows
 * still show even if the payload only embeds POs/bids on each MRQ.
 */
export const procurementItemsForPlant = (plantData, category) => {
  const rawItems =
    plantData && plantData !== "loading"
      ? plantData[category.apiKey]
      : undefined;
  let items = Array.isArray(rawItems) ? rawItems : [];
  if (items.length === 0 && plantData && plantData !== "loading") {
    const mrqs = Array.isArray(plantData.mrqs) ? plantData.mrqs : [];
    if (category.key === "po") {
      items = mrqs.flatMap((mrq) =>
        Array.isArray(mrq?.pos)
          ? mrq.pos
          : Array.isArray(mrq?.POs)
            ? mrq.POs
            : Array.isArray(mrq?.po)
              ? mrq.po
              : [],
      );
    } else if (category.key === "vendorBidEvaluation") {
      items = mrqs
        .filter((mrq) => {
          const bids = Array.isArray(mrq?.bids)
            ? mrq.bids
            : Array.isArray(mrq?.bid)
              ? mrq.bid
              : Array.isArray(mrq?.Bids)
                ? mrq.Bids
                : null;
          return Array.isArray(bids) && bids.length > 0;
        })
        .map((mrq) => ({
          id: mrq?.id,
          mrqId: mrq?.id,
          mrqNumber: mrq?.mrqNumber,
          rev: mrq?.rev,
          revision: mrq?.revision,
          mrq,
        }));
    }
  }
  return items;
};

/**
 * Row label for one procurement register entry.
 *
 * PO rows show the PO number + revision; bid-evaluation rows are one per
 * MRQ that has vendor bids, so they show that MRQ's number + revision.
 */
export const procurementItemDisplayText = (category, item, rowId) => {
  let number;
  let revision;
  if (category.key === "po") {
    number =
      item.poNumber || item.number || item.code || item.name || `#${rowId}`;
    revision =
      item.revision ??
      item.rev ??
      item.poRevision ??
      item.revisionNumber ??
      "";
  } else if (category.key === "vendorBidEvaluation") {
    const sourceMrq =
      item.mrq && typeof item.mrq === "object" ? item.mrq : null;
    number =
      item.mrqNumber ||
      sourceMrq?.mrqNumber ||
      item.number ||
      item.code ||
      item.name ||
      `#${rowId}`;
    revision =
      item.rev ??
      item.revision ??
      item.mrqRevision ??
      sourceMrq?.rev ??
      sourceMrq?.revision ??
      "";
  } else {
    number =
      item.mrqNumber ||
      item.poNumber ||
      item.mrvNumber ||
      item.srvNumber ||
      item.number ||
      item.code ||
      item.name ||
      `#${rowId}`;
    revision =
      item.mrqRevision ??
      item.poRevision ??
      item.mrvRevision ??
      item.srvRevision ??
      item.revision ??
      item.rev ??
      item.revisionNumber ??
      "";
  }
  return revision !== "" && revision != null
    ? `${number} (Rev ${revision})`
    : String(number);
};

const procurementDetailParamName = (categoryKey) => {
  if (categoryKey === "mrq") return ":mrqId";
  if (categoryKey === "po") return ":poId";
  if (categoryKey === "mrv") return ":mrvId";
  if (categoryKey === "srv") return ":srvId";
  if (categoryKey === "vendorBidEvaluation") return ":bidId";
  return null;
};

/**
 * Detail-page URL for a clicked procurement leaf, or null when the click
 * carries no item / names nothing navigable (the folder rows).
 */
export const procurementItemHref = (selectionTarget) => {
  const category = PROCUREMENT_CATEGORIES.find(
    (candidate) => candidate.key === selectionTarget.procurementType,
  );
  if (!category || !selectionTarget.itemId) return null;
  const paramName = procurementDetailParamName(category.key);
  if (!paramName) return null;
  return paths[category.pathKey]
    .replace(":plantId", selectionTarget.plantId)
    .replace(paramName, selectionTarget.itemId);
};
